import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';
import { loadPromoPriceCache, priceFromStorefrontLine, savePromoPriceCache } from '@theme/promo-price-cache';
import { resolveStorefrontOrigin, ShadowCartClient } from '@theme/shadow-cart-client';

/**
 * @typedef {Object} LivePromoPrice
 * @property {number} originalPrice
 * @property {number} finalPrice
 * @property {boolean} hasDiscount
 */

const BATCH_SIZE = 10;

function livePrice(originalPrice, finalPrice) {
  return {
    originalPrice,
    finalPrice,
    hasDiscount: finalPrice < originalPrice,
  };
}

/** @param {{ code?: string, message?: string }} error */
function isMerchandiseUserError(error) {
  const text = `${error?.code ?? ''} ${error?.message ?? ''}`;
  if (/cart does not exist|CART_NOT_FOUND|LINE_LIMIT|THROTTLED|rate limit/i.test(text)) {
    return false;
  }
  return (
    /MERCHANDISE/i.test(text) ||
    /sold out/i.test(text) ||
    /not available/i.test(text) ||
    /unpublished/i.test(text) ||
    /invalid (merchandise|variant|id)/i.test(text) ||
    /does not exist/i.test(error?.message ?? '')
  );
}

/** @param {{ unauthorized?: boolean, cartMissing?: boolean, lineLimit?: boolean, userErrors?: { code?: string, message?: string }[] }} result */
function shouldApplyCatalogFallback(result) {
  if (!result || result.unauthorized || result.cartMissing || result.lineLimit) return false;
  const errors = Array.isArray(result.userErrors) ? result.userErrors : [];
  return errors.some(isMerchandiseUserError);
}

function themeSettings() {
  return typeof Theme !== 'undefined' ? Theme : globalThis.Theme;
}

function storefrontConfig() {
  const storefront = themeSettings()?.storefront;
  const token = storefront?.token;
  if (!token) return null;
  return {
    domain: storefront.domain,
    token,
    apiVersion: storefront.apiVersion || '2026-07',
  };
}

class LivePromoPricingController {
  /** @type {Map<number, LivePromoPrice>} */
  #cache = new Map();
  /** @type {Set<number>} */
  #queuedVariantIds = new Set();
  /** @type {Map<number, Set<(price: LivePromoPrice | null) => void>>} */
  #waiters = new Map();
  /** @type {Promise<void> | null} */
  #flushPromise = null;
  #inFlight = false;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #disabled = false;
  #unauthorized = false;
  #shopperCartSeeded = false;
  #cartUpdateBound = false;
  /** @type {Set<number>} */
  #shadowAddedIds = new Set();
  /** @type {Promise<void> | null} */
  #seedPromise = null;
  /** @type {Map<number, number>} */
  #catalogByVariant = new Map();
  /** @type {ReturnType<typeof loadPromoPriceCache> | null} */
  #storageState = null;
  /** @type {ShadowCartClient | null} */
  #client = null;

  #ensureClient() {
    if (this.#unauthorized) {
      this.#disabled = true;
      return false;
    }

    const config = storefrontConfig();
    if (!config) {
      this.#disabled = true;
      return false;
    }

    this.#disabled = false;
    this.#client =
      this.#client ??
      new ShadowCartClient({
        domain: config.domain,
        token: config.token,
        apiVersion: config.apiVersion,
        origin: resolveStorefrontOrigin(config.domain, globalThis.location),
      });
    this.#ensureShopperCartSeeded();
    this.#listenForCartUpdates();
    return true;
  }

  /**
   * @param {HTMLElement} [root]
   */
  observe(root = document) {
    if (!this.#ensureClient()) return;

    const containers = root.querySelectorAll('[ref="priceContainer"][data-promo-resolved="false"]');
    if (containers.length === 0) return;

    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const container = entry.target;
            if (!(container instanceof HTMLElement)) continue;
            this.resolveContainer(container).then((resolved) => {
              if (resolved || container.dataset.promoResolved === 'true') {
                this.#observer?.unobserve(container);
                return;
              }

              const retries = Number(container.dataset.promoRetries || 0);
              if (retries >= 3) {
                this.#observer?.unobserve(container);
                return;
              }

              container.dataset.promoRetries = String(retries + 1);
              setTimeout(() => {
                if (container.dataset.promoResolved === 'true') {
                  this.#observer?.unobserve(container);
                  return;
                }
                this.resolveContainer(container).then((ok) => {
                  if (ok) this.#observer?.unobserve(container);
                });
              }, 400 * (retries + 1));
            });
          }
        },
        { rootMargin: '200px' }
      );
    }

    containers.forEach((container) => {
      this.#observer?.observe(container);
    });
  }

  /**
   * @param {HTMLElement} priceContainer
   */
  resolveContainer(priceContainer) {
    if (!this.#ensureClient()) return Promise.resolve(false);
    if (priceContainer.dataset.promoResolved === 'true') return Promise.resolve(true);

    const variantIds = this.#getVariantIdsForContainer(priceContainer);
    if (variantIds.length === 0) return Promise.resolve(false);

    return Promise.all(variantIds.map((variantId) => this.#getPrice(variantId))).then((prices) => {
      if (prices.some((price) => !price)) return false;

      /** @type {Map<number, LivePromoPrice>} */
      const priceByVariant = new Map();
      variantIds.forEach((variantId, index) => {
        const price = prices[index];
        if (price) priceByVariant.set(variantId, price);
      });
      this.#applyToContainer(priceContainer, priceByVariant);
      return true;
    });
  }

  /**
   * @param {HTMLElement} priceContainer
   * @returns {number[]}
   */
  #getVariantIdsForContainer(priceContainer) {
    /** @type {number[]} */
    const variantIds = [];

    const primaryVariantId = Number(priceContainer.dataset.variantId);
    if (primaryVariantId) {
      variantIds.push(primaryVariantId);
      const catalog = Number(priceContainer.dataset.catalogPrice);
      if (catalog) this.#catalogByVariant.set(primaryVariantId, catalog);
    }

    if (priceContainer.dataset.hasVolumePricing === 'true') {
      const minVariantId = Number(priceContainer.dataset.priceMinVariantId);
      const maxVariantId = Number(priceContainer.dataset.priceMaxVariantId);

      if (minVariantId) variantIds.push(minVariantId);
      if (maxVariantId && maxVariantId !== minVariantId) variantIds.push(maxVariantId);
    }

    return [...new Set(variantIds)];
  }

  /** @param {number} variantId */
  #getPrice(variantId) {
    const cached = this.#cache.get(variantId);
    if (cached) return Promise.resolve(cached);

    const stored = this.#readStorage().prices[String(variantId)];
    if (stored) {
      const price = livePrice(stored.originalPrice, stored.finalPrice);
      this.#cache.set(variantId, price);
      return Promise.resolve(price);
    }

    return new Promise((resolve) => {
      if (!this.#waiters.has(variantId)) {
        this.#waiters.set(variantId, new Set());
      }
      this.#waiters.get(variantId)?.add(resolve);
      this.#queuedVariantIds.add(variantId);
      this.#scheduleFlush();
    });
  }

  #readStorage() {
    const currency = themeSettings()?.money?.currency || 'USD';
    if (this.#storageState?.currency === currency) return this.#storageState;
    this.#storageState = loadPromoPriceCache(window.localStorage, currency, Date.now());
    for (const [variantId, stored] of Object.entries(this.#storageState.prices)) {
      this.#cache.set(Number(variantId), livePrice(stored.originalPrice, stored.finalPrice));
    }
    return this.#storageState;
  }

  /**
   * @param {number} variantId
   * @param {LivePromoPrice} price
   */
  #writePrice(variantId, price) {
    this.#cache.set(variantId, price);
    const state = this.#readStorage();
    state.prices[String(variantId)] = {
      originalPrice: price.originalPrice,
      finalPrice: price.finalPrice,
    };
    this.#storageState = savePromoPriceCache(window.localStorage, state, Date.now());
  }

  #scheduleFlush() {
    if (this.#flushPromise) return;

    this.#flushPromise = Promise.resolve().then(async () => {
      this.#flushPromise = null;
      await this.#flush();
    });
  }

  async #flush() {
    if (this.#inFlight || this.#queuedVariantIds.size === 0) return;

    this.#inFlight = true;
    const variantIds = [...this.#queuedVariantIds];
    this.#queuedVariantIds.clear();

    /** @param {Map<number, LivePromoPrice> | null} priceById */
    const resolveWaiters = (priceById) => {
      for (const variantId of variantIds) {
        const price = this.#cache.get(variantId) ?? priceById?.get(variantId) ?? null;
        this.#waiters.get(variantId)?.forEach((resolve) => resolve(price));
        this.#waiters.delete(variantId);
      }
    };

    try {
      if (this.#disabled) {
        resolveWaiters(null);
        return;
      }

      await this.#ensureShopperCartSeeded();
      const prices = await this.#fetchLivePrices(variantIds);

      for (const variantId of variantIds) {
        const cached = this.#cache.get(variantId);
        if (cached) {
          this.#waiters.get(variantId)?.forEach((resolve) => resolve(cached));
          this.#waiters.delete(variantId);
          continue;
        }

        const price = prices.get(variantId) ?? null;
        if (price) this.#writePrice(variantId, price);
        this.#waiters.get(variantId)?.forEach((resolve) => resolve(price));
        this.#waiters.delete(variantId);
      }
    } catch {
      for (const variantId of variantIds) {
        this.#waiters.get(variantId)?.forEach((resolve) => resolve(null));
        this.#waiters.delete(variantId);
      }
    } finally {
      this.#inFlight = false;

      if (this.#queuedVariantIds.size > 0) {
        this.#scheduleFlush();
      }
    }
  }

  /**
   * @param {number[]} variantIds
   * @returns {Promise<Map<number, LivePromoPrice>>}
   */
  async #fetchLivePrices(variantIds) {
    /** @type {Map<number, LivePromoPrice>} */
    const results = new Map();
    if (!this.#client) return results;

    const toFetch = [];
    for (const variantId of variantIds) {
      const cached = this.#cache.get(variantId);
      if (cached) {
        results.set(variantId, cached);
        continue;
      }
      if (this.#shadowAddedIds.has(variantId)) continue;
      toFetch.push(variantId);
    }
    if (toFetch.length === 0) return results;

    const state = this.#readStorage();
    let cartId = state.cartId;
    if (!cartId) {
      const created = await this.#client.createCart();
      if (created.unauthorized) {
        this.#markUnauthorized();
        return results;
      }
      if (!created.cartId) return results;
      cartId = created.cartId;
      state.cartId = cartId;
      this.#storageState = savePromoPriceCache(window.localStorage, state, Date.now());
    }

    for (let index = 0; index < toFetch.length; index += BATCH_SIZE) {
      const batch = toFetch.slice(index, index + BATCH_SIZE).filter((variantId) => {
        return !this.#cache.has(variantId) && !this.#shadowAddedIds.has(variantId);
      });
      if (batch.length === 0) continue;

      let result = await this.#client.addLines(cartId, batch);

      if (result.unauthorized) {
        this.#markUnauthorized();
        return results;
      }

      let retried = false;
      if (result.cartMissing || result.lineLimit) {
        const created = await this.#client.createCart();
        if (created.unauthorized) {
          this.#markUnauthorized();
          return results;
        }
        if (!created.cartId) return results;
        cartId = created.cartId;
        state.cartId = cartId;
        this.#storageState = savePromoPriceCache(window.localStorage, state, Date.now());
        result = await this.#client.addLines(cartId, batch);
        retried = true;
        if (result.unauthorized) {
          this.#markUnauthorized();
          return results;
        }
        if (result.cartMissing || result.lineLimit) return results;
      }

      const currency = themeSettings()?.money?.currency || 'USD';
      const returnedIds = new Set();

      for (const line of result.lines) {
        const parsed = priceFromStorefrontLine(line, currency, convertMoneyToMinorUnits);
        if (!parsed) continue;
        returnedIds.add(parsed.variantId);
        if (this.#cache.has(parsed.variantId)) continue;
        results.set(parsed.variantId, livePrice(parsed.originalPrice, parsed.finalPrice));
      }

      if (!result.cartMissing && !result.lineLimit) {
        for (const variantId of batch) this.#shadowAddedIds.add(variantId);
      }

      if (retried && returnedIds.size === 0 && !shouldApplyCatalogFallback(result)) {
        return results;
      }

      if (shouldApplyCatalogFallback(result)) {
        for (const variantId of batch) {
          if (returnedIds.has(variantId) || results.has(variantId) || this.#cache.has(variantId)) {
            continue;
          }
          const catalog = this.#catalogByVariant.get(variantId) || 0;
          results.set(variantId, livePrice(catalog, catalog));
        }
      }
    }

    return results;
  }

  #markUnauthorized() {
    this.#unauthorized = true;
    this.#disabled = true;
  }

  #ensureShopperCartSeeded() {
    this.#seedPromise ??= this.#seedShopperCart();
    return this.#seedPromise;
  }

  async #seedShopperCart() {
    if (this.#shopperCartSeeded || this.#disabled) return;
    this.#shopperCartSeeded = true;

    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      this.#ingestShopperItems(cart?.items);
    } catch {
      // Read-only seed; ignore failures.
    }
  }

  #listenForCartUpdates() {
    if (this.#cartUpdateBound) return;
    this.#cartUpdateBound = true;
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      this.#ingestShopperItems(event.detail?.resource?.items);
    });
  }

  /** @param {Array<{ variant_id: number, original_price: number, final_price: number }> | undefined} items */
  #ingestShopperItems(items) {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item?.variant_id) continue;
      const price = livePrice(item.original_price, item.final_price);
      this.#writePrice(item.variant_id, price);
    }
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {Map<number, LivePromoPrice>} priceByVariant
   */
  #applyToContainer(priceContainer, priceByVariant) {
    priceContainer.dataset.promoResolved = 'true';

    const compareAtPrice = Number(priceContainer.dataset.compareAtPrice || 0);
    const showSalePriceFirst = priceContainer.dataset.showSalePriceFirst === 'true';
    const useCurrency = priceContainer.dataset.useCurrency === 'true';
    const moneyFormat = useCurrency ? Theme.money.withCurrencyFormat : Theme.money.format;
    const currency = Theme.money.currency;

    if (priceContainer.dataset.hasVolumePricing === 'true') {
      this.#applyVolumePricing(priceContainer, priceByVariant, compareAtPrice, moneyFormat, currency);
      return;
    }

    const primaryVariantId = Number(priceContainer.dataset.variantId);
    const price = priceByVariant.get(primaryVariantId);
    if (!price?.hasDiscount) return;

    const referencePrice = Math.max(price.originalPrice, compareAtPrice);
    this.#showSalePrice(
      priceContainer,
      formatMoney(price.finalPrice, moneyFormat, currency),
      formatMoney(referencePrice, moneyFormat, currency),
      showSalePriceFirst
    );
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {Map<number, LivePromoPrice>} priceByVariant
   * @param {number} compareAtPrice
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #applyVolumePricing(priceContainer, priceByVariant, compareAtPrice, moneyFormat, currency) {
    const minVariantId = Number(priceContainer.dataset.priceMinVariantId);
    const maxVariantId = Number(priceContainer.dataset.priceMaxVariantId);
    const minPrice = priceByVariant.get(minVariantId);
    const maxPrice = priceByVariant.get(maxVariantId);

    if (!minPrice || !maxPrice) return;
    if (!minPrice.hasDiscount && !maxPrice.hasDiscount) return;

    const compareGroup = priceContainer.querySelector('[data-volume-compare-group]');
    if (compareGroup instanceof HTMLElement) {
      compareGroup.classList.remove('hidden');
    }

    const catalogMin = Number(priceContainer.dataset.catalogPriceMin || minPrice.originalPrice);
    const catalogMax = Number(priceContainer.dataset.catalogPriceMax || maxPrice.originalPrice);
    const referenceMin = Math.max(catalogMin, compareAtPrice);
    const referenceMax = Math.max(catalogMax, compareAtPrice);

    const compareEl = priceContainer.querySelector('.compare-at-price');
    const rangeEl = priceContainer.querySelector('.price-item__group .price');

    if (compareEl instanceof HTMLElement) {
      compareEl.textContent = `${formatMoney(referenceMin, moneyFormat, currency)} - ${formatMoney(
        referenceMax,
        moneyFormat,
        currency
      )}`;
    }

    if (rangeEl instanceof HTMLElement) {
      rangeEl.textContent = `${formatMoney(minPrice.finalPrice, moneyFormat, currency)} - ${formatMoney(
        maxPrice.finalPrice,
        moneyFormat,
        currency
      )}`;
    }
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {string} salePrice
   * @param {string} referencePrice
   * @param {boolean} showSalePriceFirst
   */
  #showSalePrice(priceContainer, salePrice, referencePrice, showSalePriceFirst) {
    const regular = priceContainer.querySelector('.price__regular');
    const sale = priceContainer.querySelector('.price__sale');

    if (regular instanceof HTMLElement) regular.classList.add('price__hidden');
    if (!(sale instanceof HTMLElement)) return;

    sale.classList.remove('price__hidden');

    sale.querySelectorAll('.price-item--sale').forEach((element) => {
      element.textContent = salePrice;
    });
    sale.querySelectorAll('.compare-at-price').forEach((element) => {
      element.textContent = referencePrice;
    });

    if (showSalePriceFirst) return;

    const groups = sale.querySelectorAll('.price-item__group');
    if (groups.length === 2) {
      sale.insertBefore(groups[1], groups[0]);
    }
  }
}

export const livePromoPricing = new LivePromoPricingController();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    livePromoPricing.observe();
  });
} else {
  livePromoPricing.observe();
}

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  requestAnimationFrame(() => {
    livePromoPricing.observe();
  });
});
